import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium transition-colors duration-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-teal-deep",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-line-control bg-card text-ink hover:bg-mist",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-mist/90",
        create:
          "bg-success-primary text-primary-foreground hover:bg-success-dark",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        dashed:
          "border border-dashed border-line-dashed bg-card text-ink-secondary hover:bg-mist",
        link: "text-teal underline-offset-4 hover:underline hover:text-teal-deep",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  iconSize?: "xs" | "sm" | "md" | "lg" | "xl";
  iconGap?: "none" | "xs" | "sm" | "md" | "lg";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      icon,
      iconPosition = "left",
      iconSize = "sm",
      iconGap = "md",
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const iconSizeClassMap: Record<
      NonNullable<ButtonProps["iconSize"]>,
      string
    > = {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
      xl: "h-7 w-7",
    };

    const gapXClassMap: Record<NonNullable<ButtonProps["iconGap"]>, string> = {
      none: "gap-x-0",
      xs: "gap-x-1",
      sm: "gap-x-1.5",
      md: "gap-x-2",
      lg: "gap-x-3",
    };

    const iconMarginClassMap: Record<
      NonNullable<ButtonProps["iconGap"]>,
      { left: string; right: string }
    > = {
      none: { left: "mr-0", right: "ml-0" },
      xs: { left: "mr-1", right: "ml-1" },
      sm: { left: "mr-1.5", right: "ml-1.5" },
      md: { left: "mr-2", right: "ml-2" },
      lg: { left: "mr-3", right: "ml-3" },
    };

    const horizontalGapOverride = gapXClassMap[iconGap];

    let renderedIcon: React.ReactNode = null;
    if (icon) {
      const marginClass =
        iconPosition === "left"
          ? iconMarginClassMap[iconGap].left
          : iconMarginClassMap[iconGap].right;
      if (React.isValidElement(icon)) {
        const existingClass = (icon.props as any)?.className ?? "";
        renderedIcon = (
          <span aria-hidden className={cn("shrink-0", marginClass)}>
            {React.cloneElement(icon as React.ReactElement<any>, {
              className: cn(existingClass, iconSizeClassMap[iconSize]),
            })}
          </span>
        );
      } else {
        renderedIcon = (
          <span
            aria-hidden
            className={cn("shrink-0", iconSizeClassMap[iconSize], marginClass)}
          >
            {icon}
          </span>
        );
      }
    }

    const content = (
      <>
        {iconPosition === "left" && renderedIcon}
        {children && (
          <span className="inline-flex items-center">{children}</span>
        )}
        {iconPosition === "right" && renderedIcon}
      </>
    );

    return (
      <Comp
        className={buttonVariants({
          variant,
          size,
          className: cn(horizontalGapOverride, className),
        })}
        ref={ref}
        {...props}
      >
        {content}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
